import * as React from "react";
import { App } from "antd";
import { useContentSlideStore } from "@/state/contentSlideStore";
import { useNewPrecedentSlideStore } from "@/state/newPrecedentSlideStore";
import {
  CALL_OUT_AND_SUMMARY_MUTEX_MESSAGE,
  chartFeatureMutexAllowsSelection,
  chartFeatureSlotToBinary,
  getFormattingFeatureSections,
} from "@/Popups/contentSlide/newPrecedentSlide/steps/formattingFeaturesConfig";
import { FormattingFeatureQuestionBlock } from "@/Popups/contentSlide/components/FormattingFeatureQuestionBlock";
import DropdownMenuField from "@/components/ui/DropdownMenuField";

const sharePriceTypeItems = [
  { key: "closing_share_price", label: "Closing Share price" },
  { key: "adjusted_closing_share_price", label: "Adjusted Closing Share Price" },
];

const sharePriceCurrencyItems = [
  { key: "USD", label: "USD" },
  { key: "EUR", label: "EUR" },
  { key: "GBP", label: "GBP" },
];

export default function SlideEditCustomizationDesignStep() {
  const { message } = App.useApp();
  const isKeyEvent = useNewPrecedentSlideStore((s) => s.isKeyEvent);
  const chartCount = useNewPrecedentSlideStore((s) => s.chartCount);
  const chartConfig = useNewPrecedentSlideStore((s) => s.chartConfig);
  const setChartFeatureFlag = useNewPrecedentSlideStore((s) => s.setChartFeatureFlag);
  const updateChartFeatureUserInput = useNewPrecedentSlideStore((s) => s.updateChartFeatureUserInput);
  const sharePriceType = useNewPrecedentSlideStore((s) => s.sharePriceType);
  const setSharePriceType = useNewPrecedentSlideStore((s) => s.setSharePriceType);
  const sharePriceCurrency = useNewPrecedentSlideStore((s) => s.sharePriceCurrency);
  const setSharePriceCurrency = useNewPrecedentSlideStore((s) => s.setSharePriceCurrency);
  const primaryCompanyName = useContentSlideStore((s) => s.primaryCompany?.companyName ?? null);

  const formattingChartIndices: readonly (0 | 1)[] = chartCount === 2 ? [0, 1] : [0];
  const formattingSections = React.useMemo(() => getFormattingFeatureSections(isKeyEvent), [isKeyEvent]);

  return (
    <div className="box-border h-full min-h-0 overflow-y-auto">
      <div className="p-4 text-sm text-gray-800">
        <div>
          <div className="mb-2 text-base font-bold text-gray-800">Share Price Characteristics</div>
          <div className="mb-2 flex w-4/5 flex-col gap-2">
            <DropdownMenuField
              title="Share Price Type:"
              itemslist={sharePriceTypeItems}
              initialValue={sharePriceType}
              onSelect={(value: string) => setSharePriceType(value)}
            />
            <DropdownMenuField
              title="Share Price Currency:"
              itemslist={sharePriceCurrencyItems}
              initialValue={sharePriceCurrency}
              onSelect={(value: string) => setSharePriceCurrency(value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 pr-1">
          {formattingChartIndices.map((chartIndex) => {
            if (chartIndex === 1 && chartConfig.chart2 === undefined) return null;
            const features = chartIndex === 0 ? chartConfig.chart1.features : chartConfig.chart2!.features;
            return (
              <div key={chartIndex} className="bg-white">
                <div className="mb-2 text-base font-bold text-gray-800">
                  Chart placeholder (#{chartIndex + 1})
                </div>
                <div className="flex w-full flex-wrap justify-center gap-6">
                  {formattingSections.map((section) => {
                    const entry = features[section.featureKey];
                    const userInput = entry.binary === true ? entry.userInput : undefined;
                    return (
                      <FormattingFeatureQuestionBlock
                        key={`${chartIndex}-${section.id}`}
                        section={section}
                        chartIndex={chartIndex}
                        contextHelpFlow="slideEdit"
                        primaryCompanyName={primaryCompanyName}
                        value={chartFeatureSlotToBinary(entry)}
                        userInput={userInput}
                        onUserInputChange={(text) =>
                          updateChartFeatureUserInput(chartIndex as 0 | 1, section.featureKey, text)
                        }
                        onSetValue={(isYes) => {
                          if (!chartFeatureMutexAllowsSelection(features, section.featureKey, isYes)) {
                            message.warning(CALL_OUT_AND_SUMMARY_MUTEX_MESSAGE);
                            return;
                          }
                          setChartFeatureFlag(chartIndex as 0 | 1, section.featureKey, isYes);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
